"use client";
import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { LibraryContext } from "@/native-shell/library";
import { offlineContext, OFFLINE_ACCOUNT_EVENT } from "@/lib/offline/context";

export const freestyleButton =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-teal disabled:opacity-50";
export const freestyleField =
  "mt-1 min-h-11 w-full rounded-lg border border-border bg-white px-3 py-2 text-base text-navy";
export const freestyleMessage = (error: unknown) =>
  error instanceof Error
    ? error.message
    : "This change could not be confirmed. Refresh before retrying.";

export function FreestyleNav({
  current,
}: {
  current: "feed" | "clips" | "reports" | "results";
}) {
  return (
    <nav aria-label="Freestyle views" className="mb-6 flex flex-wrap gap-2">
      <Link className={freestyleButton} href="/companion/people">
        People
      </Link>
      {(
        [
          ["feed", "Watch clips", "/companion/people/freestyle/feed"],
          ["clips", "My clips", "/companion/people/freestyle"],
          [
            "results",
            "Results & trophies",
            "/companion/people/freestyle/results",
          ],
          [
            "reports",
            "Reports & decisions",
            "/companion/people/freestyle/reports",
          ],
        ] as const
      ).map(([key, label, href]) => (
        <Link
          key={key}
          className={`${freestyleButton} ${current === key ? "!border-navy !bg-navy !text-white" : ""}`}
          aria-current={current === key ? "page" : undefined}
          href={href}
        >
          {label}
        </Link>
      ))}
    </nav>
  );
}

/** Hide private presentation immediately on account, visibility or network changes. */
export function useFreestyleAccount() {
  const [context, setContext] = useState<LibraryContext | null>(null),
    [visible, setVisible] = useState(false),
    [error, setError] = useState<string | null>(null);
  const epoch = useRef(0);
  useEffect(() => {
    let stopped = false;
    const load = async () => {
      const run = ++epoch.current;
      setContext(null);
      setError(null);
      try {
        const next = await offlineContext();
        if (!stopped && run === epoch.current) setContext(next);
      } catch (problem) {
        if (!stopped && run === epoch.current)
          setError(freestyleMessage(problem));
      }
    };
    const activity = () => setVisible(!document.hidden && navigator.onLine);
    activity();
    void load();
    window.addEventListener(OFFLINE_ACCOUNT_EVENT, load);
    window.addEventListener("online", activity);
    window.addEventListener("offline", activity);
    document.addEventListener("visibilitychange", activity);
    const cleanupSequence = epoch;
    return () => {
      stopped = true;
      cleanupSequence.current++;
      window.removeEventListener(OFFLINE_ACCOUNT_EVENT, load);
      window.removeEventListener("online", activity);
      window.removeEventListener("offline", activity);
      document.removeEventListener("visibilitychange", activity);
    };
  }, []);
  return { context, visible, error };
}

export function FreestyleGate({
  context,
  error,
  children,
  returnTo = "/companion/people/freestyle/feed",
}: {
  returnTo?: string;
  context: LibraryContext | null;
  error: string | null;
  children: ReactNode;
}) {
  if (error)
    return (
      <p role="alert" className="rounded-xl bg-warm-bg p-5">
        {error}
      </p>
    );
  if (!context) return <p role="status">Checking your account…</p>;
  if (context.owner === "device")
    return (
      <div className="rounded-2xl border border-border bg-white p-6">
        <h2 className="font-heading text-xl font-bold">
          Your people. Their best moments.
        </h2>
        <p className="my-3 text-sm leading-6">
          Sign in to watch clips shared with your Squads and resort communities.
          PeakWave is free.
        </p>
        <Link
          className={freestyleButton}
          href={`/login?redirect=${encodeURIComponent(returnTo)}`}
        >
          Sign in
        </Link>
      </div>
    );
  return children;
}

export function FreestyleDialog({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement | null>(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      aria-label={title}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      className="ph-no-capture m-auto max-h-[88dvh] w-[calc(100%_-_2rem)] max-w-xl overflow-y-auto rounded-2xl border border-border bg-white p-5 text-navy shadow-xl backdrop:bg-navy/60"
    >
      <div className="mb-5 flex items-start justify-between gap-3">
        <h2 className="font-heading text-xl font-bold">{title}</h2>
        <button type="button" className={freestyleButton} onClick={close}>
          Close
        </button>
      </div>
      {children}
    </dialog>
  );
}

export function FreestyleVideo({
  media,
  description,
  close,
}: {
  media: { video: string; poster: string; expiresAt: string };
  description: string;
  close: () => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null),
    closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  }, [close]);
  useEffect(() => {
    const element = ref.current;
    const timer = setTimeout(
      () => closeRef.current(),
      Math.max(0, Date.parse(media.expiresAt) - Date.now()),
    );
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) element?.pause();
      },
      { threshold: 0.25 },
    );
    if (element) observer.observe(element);
    const hide = () => {
      if (document.hidden || !navigator.onLine) closeRef.current();
    };
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("offline", hide);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      element?.pause();
      element?.removeAttribute("src");
      element?.load();
      document.removeEventListener("visibilitychange", hide);
      window.removeEventListener("offline", hide);
    };
  }, [media]);
  return (
    <div className="bg-navy">
      <video
        ref={ref}
        src={media.video}
        poster={media.poster}
        aria-label={description}
        className="aspect-video w-full object-contain"
        controls
        autoPlay
        muted
        playsInline
        preload="metadata"
        controlsList="nodownload"
      />
      <p className="px-4 py-2 text-xs text-white/80">
        Sound starts muted. Use the player controls to listen.
      </p>
    </div>
  );
}
